/**
 * Browser-side telemetry for diagnosing auth failures.
 *
 * Wires the Application Insights JS SDK to MSAL event callbacks. Connection
 * string is read from `VITE_APPINSIGHTS_CONNECTION_STRING` at build time; if
 * absent, this module is a safe no-op. A prod build with no connection string
 * logs a grep-able `[TELEMETRY_CONFIG_MISSING]` error to devtools and runs as a
 * no-op — non-fatal, so a missed env var never bricks the SPA.
 *
 * Privacy (HIPAA): forwards only event names, MSAL `errorCode`, the extracted
 * `AADSTSnnnnn` code, and presence flags for MSAL sessionStorage/cookies. The
 * raw MSAL `errorMessage` is never forwarded — AADSTS messages frequently embed
 * UPN, email, tenant ID, or callback URLs. The SDK's own auto-collectors are
 * disabled in `initTelemetry` (dependency/AJAX URLs and `window.onerror`
 * messages can carry PHI), and `sanitizeTelemetryEnvelope` sets the client-IP
 * tag to a non-routable sentinel so the service records no real source IP — IP
 * and URLs are HIPAA Safe-Harbor identifiers.
 */
import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import type { IPublicClientApplication, EventMessage, EventType, AuthError } from '@azure/msal-browser'

const CONNECTION_STRING = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING as string | undefined

if (import.meta.env.PROD && !CONNECTION_STRING) {
  // Loud in devtools so a missing env var is visible during smoke-test, but
  // non-fatal — an instrumentation gap is never worth bricking the SPA. Grep
  // this id in browser logs after deploy to confirm telemetry is live.
  console.error(
    '[TELEMETRY_CONFIG_MISSING] VITE_APPINSIGHTS_CONNECTION_STRING is unset; ' +
    'auth telemetry will not be sent. Set it on the App Service.'
  )
}

let appInsights: ApplicationInsights | null = null

const MSAL_INTERACTION_STATUS_KEY = 'msal.interaction.status'

type SessionStorageSnapshot = {
  hasInteractionStatus: boolean
  msalKeyCount: number
  snapshotFailed: boolean
}

const sessionStorageSnapshot = (): SessionStorageSnapshot => {
  try {
    const keys = Object.keys(sessionStorage).filter(k => k.startsWith('msal'))
    return {
      hasInteractionStatus: sessionStorage.getItem(MSAL_INTERACTION_STATUS_KEY) !== null,
      msalKeyCount: keys.length,
      snapshotFailed: false,
    }
  } catch {
    return { hasInteractionStatus: false, msalKeyCount: 0, snapshotFailed: true }
  }
}

export const extractAadstsCode = (message: string | undefined): string | null => {
  if (!message) return null
  const match = message.match(/\bAADSTS(\d+)/)
  return match ? `AADSTS${match[1]}` : null
}

export const eventNameFor = (eventType: EventType): string => {
  // EventType values in @azure/msal-browser@3 are strings like "msal:loginSuccess".
  // Renaming the prefix to "msal." gives nicer App Insights query names without
  // affecting the underlying event identity.
  return String(eventType).replace(/^msal:/, 'msal.')
}

/**
 * Presence flags for an auth response in the URL fragment — only whether
 * `code`/`error`/`state` params are present, never their (secret) values. Lets
 * the dashboard tell the silent-null failure (handleRedirectPromise resolves
 * null while the hash still carries `#code=…`) apart from a clean idle reload.
 */
type RedirectHashFlags = {
  hashHadCode: boolean
  hashHadError: boolean
  hashHadState: boolean
}

export const redirectHashFlags = (hash: string): RedirectHashFlags => ({
  hashHadCode: /[#&]code=/.test(hash),
  hashHadError: /[#&]error=/.test(hash),
  hashHadState: /[#&]state=/.test(hash),
})

/**
 * True if any `msal.*` cookie is present — i.e. the `storeAuthStateInCookie`
 * fallback survived the cross-site round-trip. Presence only, never values.
 */
export const hasMsalAuthStateCookie = (cookie: string = document.cookie): boolean =>
  cookie.split(';').some(c => c.trimStart().startsWith('msal.'))

type BrowserContext = { browserBrand: string; cookieEnabled: boolean }

/**
 * Non-identifying browser context: the brand list (e.g. "Chromium,Google Chrome")
 * and whether cookies are enabled. The bug is browser-storage-partitioning
 * specific, so the engine is the key diagnostic dimension; brand+major is
 * aggregate, not an individual identifier.
 */
export const browserContext = (nav: Navigator = navigator): BrowserContext => {
  const brands = (nav as Navigator & { userAgentData?: { brands?: { brand: string }[] } })
    .userAgentData?.brands
  return {
    browserBrand: brands?.map(b => b.brand).join(',') ?? 'legacy',
    cookieEnabled: nav.cookieEnabled,
  }
}

/**
 * App Insights telemetry initializer. Sets the client-IP tag to a non-routable
 * sentinel so the service records no real source IP (a HIPAA Safe-Harbor
 * identifier; the browser SDK doesn't send it, the service otherwise infers it
 * from the request). Returns void, which keeps the item — returning `false`
 * would drop the telemetry.
 */
export const sanitizeTelemetryEnvelope = (item: { tags?: Record<string, unknown> }): void => {
  item.tags = item.tags ?? {}
  // Non-routable sentinel — suppresses App Insights' automatic source-IP
  // capture (0.0.0.0 is the conventional value).
  item.tags['ai.location.ip'] = '0.0.0.0'
}

/**
 * Build the App Insights properties for an MSAL event. Pure + exported so the
 * no-PII contract is unit-testable: only categorical codes + presence flags,
 * never the raw `errorMessage`.
 */
export const buildMsalEventPayload = (msg: EventMessage) => {
  const err = msg.error as AuthError | null
  const aadstsCode = extractAadstsCode(err?.errorMessage)
  // Surface format drift: errorCode present but no AADSTS extracted means MS
  // changed its message format and our only categorical signal disappeared.
  const aadstsExtractionLost = err?.errorCode != null && err.errorMessage != null && aadstsCode == null
  return {
    interactionType: msg.interactionType ?? null,
    errorCode: err?.errorCode ?? null,
    aadstsCode,
    aadstsExtractionLost,
    ...sessionStorageSnapshot(),
  }
}

const handleMsalEvent = (msg: EventMessage) => {
  if (!appInsights) return
  appInsights.trackEvent({ name: eventNameFor(msg.eventType), properties: buildMsalEventPayload(msg) })
}

/**
 * Build the sanitized exception + properties for an auth failure. Pure +
 * exported so the no-PII contract is testable. The exception message is the
 * categorical `errorCode` only — never the raw `errorMessage` (which can carry
 * UPN/email/callback URL via trackException's default .message/.stack).
 *
 * `context` is spread verbatim into the payload — callers must pass only
 * PII-free categorical values (e.g. `{ source }`), never raw error text.
 */
export const buildAuthExceptionTelemetry = (
  exception: unknown,
  context: Record<string, unknown> = {},
): { exception: Error; properties: Record<string, unknown> } => {
  const authErr = exception as Partial<AuthError>
  const errorCode = authErr?.errorCode ?? null
  const aadstsCode = extractAadstsCode(authErr?.errorMessage)
  return {
    exception: new Error(errorCode ?? 'auth_error'),
    properties: { ...sessionStorageSnapshot(), ...context, errorCode, aadstsCode },
  }
}

/** Initialize telemetry and wire MSAL event callbacks. Safe to call multiple times — only the first call subscribes. */
export const initTelemetry = (msalInstance: IPublicClientApplication): void => {
  if (appInsights || !CONNECTION_STRING) return
  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString: CONNECTION_STRING,
        enableAutoRouteTracking: false, // SPA has no router; we control page views
        // HIPAA: disable every auto-collector. We forward only hand-built,
        // PII-stripped auth events. Left on, the SDK would auto-capture
        // dependency/AJAX request URLs and window.onerror messages, both of
        // which can carry PHI (clinical error text, identifiers in URLs).
        disableAjaxTracking: true,
        disableFetchTracking: true,
        disableExceptionTracking: true,
      },
    })
    appInsights.loadAppInsights()
    // Belt-and-suspenders on the auto-collector lockdown: structurally strip the
    // client IP from every envelope, so no individual identifier is ever stored.
    appInsights.addTelemetryInitializer(sanitizeTelemetryEnvelope)
    appInsights.trackPageView({ name: 'login' })
    msalInstance.addEventCallback(handleMsalEvent)
  } catch (err) {
    // Telemetry must never break the app — surface with a distinctive id so
    // grep / browser-devtools onboarding catches it.
    console.warn('[TELEMETRY_INIT_FAILED]', err)
    appInsights = null
  }
}

/** Track a custom event. No-op if telemetry isn't initialized. */
export const trackAuthEvent = (
  name: string,
  properties: Record<string, unknown> = {},
): void => {
  if (!appInsights) return
  try {
    appInsights.trackEvent({
      name,
      properties: { ...sessionStorageSnapshot(), ...properties },
    })
  } catch {
    // Swallow — telemetry must never break the app
  }
}

/**
 * Track an auth exception. No-op if telemetry isn't initialized. Forwards only
 * the categorical code + extracted AADSTS code; see `buildAuthExceptionTelemetry`.
 */
export const trackAuthException = (
  exception: unknown,
  context: Record<string, unknown> = {},
): void => {
  if (!appInsights) return
  try {
    appInsights.trackException(buildAuthExceptionTelemetry(exception, context))
  } catch {
    // Swallow
  }
}
